/**
 * Console primitives — the same shapes as the ROP officer app's primitives
 * (apps/ROP-teams/src/components/primitives: WidgetCard, Pill, Banner) so the Risk panel's
 * verification console reads as the same product. Styles live in ./console.css.
 */

import type { ReactNode } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle, Info } from 'lucide-react';

import type { Tone } from '@/services/verificationTestApi';

import './console.css';

export function Panel({
  title, tone = 'info', actions, children,
}: { title: ReactNode; tone?: Tone; actions?: ReactNode; children: ReactNode }): JSX.Element {
  const mod = tone === 'info' ? '' : ` vc-panel--${tone}`;
  return (
    <section className={`vc-panel${mod}`}>
      <header className="vc-panel__head">
        <span className="vc-panel__title">{title}</span>
        {actions && <div className="vc-panel__actions">{actions}</div>}
      </header>
      <div className="vc-panel__body">{children}</div>
    </section>
  );
}

export function Pill({ tone, children }: { tone: Tone; children: ReactNode }): JSX.Element {
  return <span className={`vc-pill vc-pill--${tone}`}>{children}</span>;
}

const ICONS = { info: Info, warn: AlertTriangle, err: AlertOctagon, ok: CheckCircle, muted: Info } as const;

export function Banner({ tone, title, children }: { tone: Tone; title?: ReactNode; children?: ReactNode }): JSX.Element {
  const Icon = ICONS[tone];
  return (
    <div className={`vc-banner vc-banner--${tone}`} role={tone === 'err' ? 'alert' : 'status'}>
      <Icon size={16} aria-hidden style={{ flexShrink: 0, marginTop: 1 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        {title && <div className="vc-banner__title">{title}</div>}
        {children}
      </div>
    </div>
  );
}

export function Field({
  label, htmlFor, required, hint, error, children,
}: { label: string; htmlFor: string; required?: boolean; hint?: ReactNode; error?: string | null; children: ReactNode }): JSX.Element {
  return (
    <div className={`vc-field${error ? ' vc-field--error' : ''}`}>
      <label className="vc-field__label" htmlFor={htmlFor}>
        {label}{required && <span className="vc-req" aria-hidden>*</span>}
      </label>
      {children}
      {error ? <span className="vc-field__err" role="alert">{error}</span> : hint ? <span className="vc-field__hint">{hint}</span> : null}
    </div>
  );
}

export function Disclosure({ summary, children }: { summary: ReactNode; children: ReactNode }): JSX.Element {
  return (
    <details className="vc-details">
      <summary>{summary}</summary>
      <div className="vc-details__body">{children}</div>
    </details>
  );
}

/** Raw response behind a disclosure — never the primary view. */
export function RawResponse({ text, label = 'Show raw response' }: { text: string | null | undefined; label?: string }): JSX.Element | null {
  if (!text) return null;
  let pretty = text;
  try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* not JSON — show as returned */ }
  return (
    <Disclosure summary={label}>
      <pre className="vc-raw">{pretty}</pre>
    </Disclosure>
  );
}

export function SectionTitle({ children }: { children: ReactNode }): JSX.Element {
  return <h4 className="vc-section-title">{children}</h4>;
}

export function RuleSteps({ steps }: { steps: Array<{ text: string; applied: boolean }> }): JSX.Element {
  return (
    <ol className="vc-steps">
      {steps.map((s, i) => (
        <li key={i} className={s.applied ? 'is-applied' : undefined}>
          <span className="vc-steps__mark" aria-label={s.applied ? 'Applied to this run' : undefined}>{s.applied ? '▶' : '·'}</span>
          <span>{s.text}</span>
        </li>
      ))}
    </ol>
  );
}

export function SettingsTable({ rows }: { rows: Array<{ key: string; value: string | null; source: string; owner: string; effect: string }> }): JSX.Element {
  return (
    <div className="vc-table-wrap">
      <table className="vc-table">
        <thead><tr><th>Setting</th><th>Value used</th><th>Who changes it</th><th>What it does</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="vc-mono">{r.key}</td>
              <td>
                <strong>{r.value === null || r.value === '' ? '(empty)' : r.value}</strong>{' '}
                <span className="vc-muted">{r.source === 'saved' ? 'saved' : 'default'}</span>
              </td>
              <td>{r.owner}</td>
              <td>{r.effect}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function fmtDate(v: string | null | undefined, dateOnly = false): string {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return dateOnly ? d.toLocaleDateString() : d.toLocaleString();
}
